import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Button from "../Generic/Button";
import SuccessButton from "../Generic/SuccessButton";
import Loader from "../Generic/Loader";
import Checkbox from "../Generic/Checkbox";
import Dropdown from "../Generic/Dropdown";
import { errorNotification, successNotification } from "../Generic/Notifications";
import { AppRoutes, DashboardModules } from "../config/app.config";
import { Page, FormCard, CardHeader, CardTitle, Section, SectionTitle, Field, FieldLabel, FieldInput, DropdownWrap, Footer, ErrorText, Tag, TagList, TagRemove } from "./Components/shared/OfferFormLayout";
import { useOfferContext } from "./context/OfferContext";
import OffersService from "./Services/OffersService";
import { COMPATIBILITY_WILDCARD, OfferCompatibleWithField, OfferGroupField } from "./constants";
import { mapOfferGroupDraftToPayload } from "./utils/offerApiMapping";
import { createInitialOfferGroupDraft } from "./utils/offerDraft";
import { getMappingIds, isApplyAll, setApplyAll } from "./utils/offerMapping";
import {
	buildOfferGroupDropdownOptions,
	validateOfferGroupForm,
} from "./utils/offerValidation";
import { isEmpty } from "../util/util";

const IconButton = styled.span.attrs({
	className: "material-symbols-outlined",
})`
	cursor: pointer;
	color: #36395a;
	user-select: none;
	flex-shrink: 0;
	line-height: 1;
`;

const LabelRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	width: 100%;
`;

const ApplyAllLabel = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	font-size: 0.85rem;
	font-weight: 500;
	color: #36395a;
	cursor: pointer;
	white-space: nowrap;
`;

const CompactCheckbox = styled(Checkbox)`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
`;

const OfferGroupCreate = () => {
	const navigate = useNavigate();
	const { offerGroups, setOfferGroupsList } = useOfferContext();
	const [draft, setDraft] = useState(createInitialOfferGroupDraft);
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [includeSelection, setIncludeSelection] = useState("");
	const [excludeSelection, setExcludeSelection] = useState("");

	const OFFERS_ROUTES = AppRoutes[DashboardModules.OFFERS];

	useEffect(() => {
		fetchGroups();
	}, []);

	async function fetchGroups() {
		const { data, error } = await OffersService.getOfferGroups();
		if (error) {
			errorNotification(error);
			return;
		}
		setOfferGroupsList(data.items || []);
		setLoading(false);
	}

	function getGroupName(id) {
		if (id === COMPATIBILITY_WILDCARD) return "All (*)";
		const group = (offerGroups || []).find((g) => g._id === id);
		return group?.name || id;
	}

	function updateDraft(updates) {
		setDraft((prev) => ({ ...prev, ...updates }));
		setErrors({});
	}

	function updateCompatibleWith(fieldUpdates) {
		const compatibleWith = draft[OfferGroupField.COMPATIBLE_WITH] || {};
		updateDraft({
			[OfferGroupField.COMPATIBLE_WITH]: {
				...compatibleWith,
				...fieldUpdates,
			},
		});
	}

	function handleApplyAll(field, checked) {
		const oppositeField =
			field === OfferCompatibleWithField.INCLUDES
				? OfferCompatibleWithField.EXCLUDES
				: OfferCompatibleWithField.INCLUDES;
		const compatibleWith = draft[OfferGroupField.COMPATIBLE_WITH] || {};
		const next = {
			[field]: setApplyAll(checked),
		};
		if (checked && isApplyAll(compatibleWith[oppositeField])) {
			next[oppositeField] = [];
		}
		if (field === OfferCompatibleWithField.INCLUDES) {
			setIncludeSelection("");
		} else {
			setExcludeSelection("");
		}
		updateCompatibleWith(next);
	}

	function addCompatibility(field, selection, setSelection) {
		if (!selection) return;
		const compatibleWith = draft[OfferGroupField.COMPATIBLE_WITH] || {};
		if (isApplyAll(compatibleWith[field])) {
			setSelection("");
			return;
		}
		const current = getMappingIds(compatibleWith[field]);
		const oppositeField =
			field === OfferCompatibleWithField.INCLUDES
				? OfferCompatibleWithField.EXCLUDES
				: OfferCompatibleWithField.INCLUDES;
		const opposite = getMappingIds(compatibleWith[oppositeField]);
		if (current.includes(selection) || opposite.includes(selection)) {
			setSelection("");
			return;
		}
		updateCompatibleWith({
			[field]: [...current, selection],
		});
		setSelection("");
	}

	function removeCompatibility(field, id) {
		const compatibleWith = draft[OfferGroupField.COMPATIBLE_WITH] || {};
		const current = getMappingIds(compatibleWith[field]);
		updateCompatibleWith({
			[field]: current.filter((v) => v !== id),
		});
	}

	async function submitGroup() {
		const formErrors = validateOfferGroupForm(draft);
		if (!isEmpty(formErrors)) {
			setErrors(formErrors);
			return;
		}

		setSubmitting(true);
		try {
			const payload = mapOfferGroupDraftToPayload(draft);
			const { error } = await OffersService.createOfferGroup(payload);
			if (error) {
				errorNotification(error);
				return;
			}
			successNotification("Offer group created successfully");
			navigate(OFFERS_ROUTES.CREATE.getPath());
		} catch {
			errorNotification("Failed to create offer group");
		} finally {
			setSubmitting(false);
		}
	}

	if (loading) {
		return (
			<Page>
				<Loader />
			</Page>
		);
	}

	const compatibleWith = draft[OfferGroupField.COMPATIBLE_WITH] || {};
	const applyAllIncludes = isApplyAll(compatibleWith[OfferCompatibleWithField.INCLUDES]);
	const applyAllExcludes = isApplyAll(compatibleWith[OfferCompatibleWithField.EXCLUDES]);
	const includes = getMappingIds(compatibleWith[OfferCompatibleWithField.INCLUDES]);
	const excludes = getMappingIds(compatibleWith[OfferCompatibleWithField.EXCLUDES]);
	const blockedIds = new Set([...includes, ...excludes]);
	const includeOptions = buildOfferGroupDropdownOptions(
		(offerGroups || []).filter((g) => !blockedIds.has(g._id || g.id))
	);
	const excludeOptions = includeOptions;

	return (
		<Page>
			<FormCard>
				<CardHeader>
					<CardTitle>Create Offer Group</CardTitle>
				</CardHeader>

				<Section>
					<SectionTitle>Basic Information</SectionTitle>
					<Field id="offer-group-name-field">
						<FieldLabel htmlFor="offer-group-name">Group Name *</FieldLabel>
						<FieldInput
							id="offer-group-name"
							value={draft[OfferGroupField.NAME]}
							onChange={(e) =>
								updateDraft({ [OfferGroupField.NAME]: e.target.value })
							}
							errorMessage={errors[OfferGroupField.NAME]}
							errored={!!errors[OfferGroupField.NAME]}
						/>
						{errors[OfferGroupField.NAME] && (
							<ErrorText>{errors[OfferGroupField.NAME]}</ErrorText>
						)}
					</Field>
				</Section>

				<Section>
					<SectionTitle>Group Settings</SectionTitle>
					<Field id="offer-group-multiple-offers-field">
						<FieldLabel
							htmlFor="offer-group-multiple-offers"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "0.5rem",
								cursor: "pointer",
							}}>
							<Checkbox
								id="offer-group-multiple-offers"
								checked={!!draft[OfferGroupField.CAN_APPLY_MULTIPLE_OFFERS]}
								onChange={(e) =>
									updateDraft({
										[OfferGroupField.CAN_APPLY_MULTIPLE_OFFERS]: e.target.checked,
									})
								}
							/>
							Can apply multiple offers
						</FieldLabel>
					</Field>
				</Section>

				<Section>
					<SectionTitle>Compatibility Rules</SectionTitle>

					<Field id="offer-group-includes-field">
						<LabelRow>
							<FieldLabel as="span">Includes</FieldLabel>
							<ApplyAllLabel htmlFor="offer-group-apply-all-includes">
								<CompactCheckbox
									id="offer-group-apply-all-includes"
									checked={applyAllIncludes}
									onChange={(e) =>
										handleApplyAll(
											OfferCompatibleWithField.INCLUDES,
											e.target.checked
										)
									}
								/>
								Apply to all
							</ApplyAllLabel>
						</LabelRow>
						{!applyAllIncludes && (
							<>
								<div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
									<DropdownWrap id="offer-group-includes">
										<Dropdown
											head="Select group to include"
											optionsList={includeOptions}
											value={includeSelection}
											onChange={setIncludeSelection}
										/>
									</DropdownWrap>
									<IconButton
										role="button"
										aria-label="Add include"
										onClick={() =>
											addCompatibility(
												OfferCompatibleWithField.INCLUDES,
												includeSelection,
												setIncludeSelection
											)
										}>
										add
									</IconButton>
								</div>
								<TagList>
									{includes.map((id) => (
										<Tag key={id}>
											{getGroupName(id)}
											<TagRemove
												onClick={() =>
													removeCompatibility(
														OfferCompatibleWithField.INCLUDES,
														id
													)
												}>
												×
											</TagRemove>
										</Tag>
									))}
								</TagList>
							</>
						)}
					</Field>

					<Field id="offer-group-excludes-field">
						<LabelRow>
							<FieldLabel as="span">Excludes</FieldLabel>
							<ApplyAllLabel htmlFor="offer-group-apply-all-excludes">
								<CompactCheckbox
									id="offer-group-apply-all-excludes"
									checked={applyAllExcludes}
									onChange={(e) =>
										handleApplyAll(
											OfferCompatibleWithField.EXCLUDES,
											e.target.checked
										)
									}
								/>
								Apply to all
							</ApplyAllLabel>
						</LabelRow>
						{!applyAllExcludes && (
							<>
								<div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
									<DropdownWrap id="offer-group-excludes">
										<Dropdown
											head="Select group to exclude"
											optionsList={excludeOptions}
											value={excludeSelection}
											onChange={setExcludeSelection}
										/>
									</DropdownWrap>
									<IconButton
										role="button"
										aria-label="Add exclude"
										onClick={() =>
											addCompatibility(
												OfferCompatibleWithField.EXCLUDES,
												excludeSelection,
												setExcludeSelection
											)
										}>
										add
									</IconButton>
								</div>
								<TagList>
									{excludes.map((id) => (
										<Tag key={id}>
											{getGroupName(id)}
											<TagRemove
												onClick={() =>
													removeCompatibility(
														OfferCompatibleWithField.EXCLUDES,
														id
													)
												}>
												×
											</TagRemove>
										</Tag>
									))}
								</TagList>
							</>
						)}
					</Field>
				</Section>

				<Footer>
					<Button
						onClick={() => navigate(OFFERS_ROUTES.CREATE.getPath())}
						disabled={submitting}>
						Cancel
					</Button>
					<SuccessButton onClick={submitGroup} disabled={submitting}>
						Create Group
					</SuccessButton>
				</Footer>
			</FormCard>
		</Page>
	);
};

export default OfferGroupCreate;
